import React, { useState } from 'react';
import { DropdownProps, Label as SemanticLabel } from 'semantic-ui-react';
import { useAllLocales, useSelectedLocales } from '../hooks/useLocales';
import { HighDropdown } from './HighDropdown';
import { Label } from './Label';
import { Segment } from './Segment';
import { LocaleWithEmoji } from '../Domain/LocaleWithEmoji';

interface LocaleProps {
	onChange: (locales: string | string[]) => void;
}

export const Locale: React.FC<LocaleProps> = ({ onChange }) => {
	const [locales, setLocales] = useAllLocales();
	const [selectedLocales, setSelectedLocales] = useSelectedLocales();
	const [error, setError] = useState('');
	const [isOpened, setIsOpened] = useState(false);

	const options = locales.map((l) => {
		const localeWithEmoji = new LocaleWithEmoji(l);
		return { value: localeWithEmoji.baseName, text: localeWithEmoji.baseNameWithEmoji };
	});

	const isValidLocale = (value: string) => {
		try {
			Intl.DateTimeFormat.supportedLocalesOf(value);
			return true;
		} catch (e) {
			return false;
		}
	}

	const handleAdd = (_: React.KeyboardEvent<HTMLElement>, { value }: DropdownProps) => {
		const addedValue = value as string;
			
		if(isValidLocale(addedValue)) {
			setError('');
			setLocales([...locales, addedValue]);
			setSelectedLocales((Intl as any).getCanonicalLocales([...selectedLocales, addedValue]));
		} else {
			setError(`${value} is not a valid locale`);
			setIsOpened(false);
		}
	};

	const handleChange = (_: React.SyntheticEvent<HTMLElement>, p: DropdownProps) => {
		const selectedValues = p.value as string[];
		if(selectedValues.length && isValidLocale(selectedValues[selectedValues.length - 1])) {
			setSelectedLocales(selectedValues);
			updateValues(selectedValues);
		}
	};

	const updateValues = (values: string[]) => {
		onChange(values.length === 1 ? values[0] : values);
	};

	return (
		<Segment basic fluid>
			<Label>locales</Label>
			<HighDropdown
				fluid
				value={selectedLocales}
				options={options}
				allowAdditions
				selection
				search
				multiple
				placeholder="Select or add"
				error={Boolean(error.length)}
				closeOnBlur
				onChange={handleChange}
				open={isOpened}
				onOpen={() => setIsOpened(true)}
				onClose={() => setIsOpened(false)}
				onAddItem={handleAdd}
			></HighDropdown>
			{Boolean(error.length) && (
				<SemanticLabel basic color="red" pointing="left">
					{error}
				</SemanticLabel>
			)}
		</Segment>
	);
};
